import React, { useState } from 'react';
import Combobox from './Combobox';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Check, X } from 'lucide-react';
import { useDeals } from '../context/DataContext';

/**
 * BrandSelect — searchable brand picker with inline "add new brand" support.
 */
const BrandSelect = ({ value, onChange, placeholder = 'Select brand', className = '' }) => {
  const { brands, addBrand } = useDeals();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [logo, setLogo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    const clean = name.trim();
    if (!clean) { setError('Brand name required'); return; }
    setSaving(true);
    setError(null);
    try {
      const b = await addBrand(clean, logo.trim());
      onChange(b.id);
      setCreating(false);
      setName('');
      setLogo('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add brand');
    } finally {
      setSaving(false);
    }
  };

  if (creating) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="flex gap-2">
          <Input
            autoFocus
            placeholder="Brand name (e.g. Aritzia)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') { setCreating(false); setError(null); } }}
            className="flex-1"
          />
          <Input
            placeholder="Emoji"
            value={logo}
            onChange={(e) => setLogo(e.target.value)}
            className="w-16 text-center"
            maxLength={4}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-500">adding a new brand to your roster</span>
          <div className="flex gap-1">
            <Button type="button" size="sm" variant="ghost" onClick={() => { setCreating(false); setError(null); setName(''); setLogo(''); }} className="h-7 px-2">
              <X className="w-3.5 h-3.5" />
            </Button>
            <Button type="button" size="sm" onClick={submit} disabled={saving} className="h-7 px-2 bg-mint-500 hover:bg-mint-600 text-slate-900">
              <Check className="w-3.5 h-3.5 mr-1" /> {saving ? 'adding…' : 'add'}
            </Button>
          </div>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  }

  const items = brands.map((b) => ({
    value: b.id,
    label: b.logo ? `${b.logo} ${b.name}` : b.name,
    keywords: [b.name],
  }));

  return (
    <Combobox
      value={value || ''}
      onChange={onChange}
      items={items}
      placeholder={placeholder}
      emptyText="no brands match"
      triggerClassName={className}
      extraOption={{
        label: (<span className="inline-flex items-center gap-1.5 text-mint-700"><Plus className="w-3.5 h-3.5" /> add new brand…</span>),
        onSelect: () => setCreating(true),
      }}
    />
  );
};

export default BrandSelect;
